import { useCallback, type ComponentType } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Building2,
  Clock,
  Headphones,
  Home,
  Plane,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const CAR_IMAGE_SRC = "/images/search-delivery-banner-car.png";
const CAR_IMAGE_WIDTH = 874;
const CAR_IMAGE_HEIGHT = 424;
const MOROCCO_MAP_IMAGE_SRC = "/images/morocco-delivery-map.png";
const MOROCCO_MAP_WIDTH = 720;
const MOROCCO_MAP_HEIGHT = 466;

const openSearchLocationPicker = () => {
  window.dispatchEvent(new CustomEvent("rakb:open-search-location"));
};

interface SearchResultsDeliveryBannerProps {
  className?: string;
  /** Carte arrondie (legacy). `home` = section pleine largeur page d'accueil. */
  variant?: "card" | "home";
  /** @deprecated Préférer variant="home" */
  embedded?: boolean;
}

const FEATURE_ICON_CLASS =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-green-night/35 bg-white text-green-night sm:h-9 sm:w-9";

const FeatureIcon = ({
  icon: Icon,
  className,
}: {
  icon: ComponentType<{ className?: string }>;
  className?: string;
}) => (
  <span className={cn(FEATURE_ICON_CLASS, className)}>
    <Icon className="h-4 w-4" aria-hidden />
  </span>
);

const MoroccoDeliveryMap = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  return (
    <div className={cn("relative shrink-0", className)}>
      <img
        src={MOROCCO_MAP_IMAGE_SRC}
        alt={t("searchResults.deliveryBanner.mapAria")}
        width={MOROCCO_MAP_WIDTH}
        height={MOROCCO_MAP_HEIGHT}
        decoding="async"
        loading="lazy"
        className="h-full w-full object-contain object-center"
      />
    </div>
  );
};

const DeliveryBannerCar = ({ alt, className }: { alt: string; className?: string }) => (
  <div className={cn("relative mx-auto shrink-0 overflow-visible", className)}>
    <img
      src={CAR_IMAGE_SRC}
      alt={alt}
      width={CAR_IMAGE_WIDTH}
      height={CAR_IMAGE_HEIGHT}
      loading="lazy"
      decoding="async"
      className="block h-auto w-full object-contain object-center"
    />
  </div>
);

interface FeatureItemProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
}

const FeatureItem = ({ icon, title, subtitle }: FeatureItemProps) => (
  <li className="flex min-h-[44px] items-start gap-3">
    <FeatureIcon icon={icon} />
    <span className="min-w-0 flex-1 pt-1.5">
      <span className="block text-sm font-semibold leading-snug text-text-primary">{title}</span>
      <span className="mt-0.5 block text-xs leading-snug text-text-secondary">{subtitle}</span>
    </span>
  </li>
);

/** Ligne mobile : icône + titre / sous-titre alignés au centre vertical */
const MobileListRow = ({ icon, title, subtitle }: FeatureItemProps) => (
  <li className="flex items-center gap-4 py-4 first:pt-3.5 last:pb-3.5">
    <FeatureIcon icon={icon} className="h-11 w-11 rounded-xl" />
    <div className="min-w-0 flex-1 text-left">
      <p className="text-[15px] font-semibold leading-snug text-text-primary">{title}</p>
      <p className="mt-1 text-xs leading-relaxed text-text-secondary">{subtitle}</p>
    </div>
  </li>
);

/** Avantages compacts sous la carte — sans encadré gris */
const MobileBenefitPill = ({ icon, title }: { icon: FeatureItemProps["icon"]; title: string }) => (
  <li className="flex flex-col items-center gap-2 px-1 text-center">
    <FeatureIcon icon={icon} className="h-10 w-10 rounded-xl" />
    <span className="text-[11px] font-semibold leading-snug text-text-primary sm:text-xs">{title}</span>
  </li>
);

export const SearchResultsDeliveryBanner = ({
  className,
  variant = "card",
  embedded = false,
}: SearchResultsDeliveryBannerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHomeSection = variant === "home" || embedded;

  const handleBookNow = useCallback(() => {
    const isOnSearchPage = pathname === "/search" || pathname === "/cars/search";

    if (isOnSearchPage) {
      openSearchLocationPicker();
      return;
    }

    navigate("/search?focus=location");
  }, [navigate, pathname]);

  const locations = [
    { icon: Plane, titleKey: "airports" },
    { icon: Building2, titleKey: "hotels" },
    { icon: Home, titleKey: "home" },
  ] as const;

  const benefits = [
    { icon: Clock, key: "flexibility" },
    { icon: ShieldCheck, key: "premium" },
    { icon: Headphones, key: "support" },
  ] as const;

  const carAlt = t("searchResults.deliveryBanner.carAlt");

  const headline = (
    <>
      <h2 className="font-heading text-lg font-bold leading-tight tracking-tight text-text-primary sm:text-xl lg:text-[1.35rem] xl:text-2xl">
        {t("searchResults.deliveryBanner.title")}
        <br />
        <span className="text-green-night">{t("searchResults.deliveryBanner.titleHighlight")}</span>
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-text-secondary">
        {t("searchResults.deliveryBanner.subtitle")}
      </p>
    </>
  );

  const content = (
    <>
        {/* Mobile & tablette (< lg) */}
        <div className="flex flex-col gap-5 lg:hidden sm:gap-6">
          <div className="min-w-0 text-left">{headline}</div>

          <div className="flex w-full justify-center">
            <DeliveryBannerCar
              alt={carAlt}
              className="w-full max-w-[252px] sm:max-w-[288px]"
            />
          </div>

          <div className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-sm ring-1 ring-black/[0.04] sm:rounded-3xl">
            <ul className="divide-y divide-border/50 px-4 sm:px-5">
              {locations.map(({ icon, titleKey }) => (
                <MobileListRow
                  key={titleKey}
                  icon={icon}
                  title={t(`searchResults.deliveryBanner.locations.${titleKey}.title`)}
                  subtitle={t(`searchResults.deliveryBanner.locations.${titleKey}.subtitle`)}
                />
              ))}
            </ul>
          </div>

          <ul className="grid grid-cols-3 gap-2 sm:gap-3">
            {benefits.map(({ icon, key }) => (
              <MobileBenefitPill
                key={key}
                icon={icon}
                title={t(`searchResults.deliveryBanner.benefits.${key}.title`)}
              />
            ))}
          </ul>

          <Button
            type="button"
            onClick={handleBookNow}
            className="h-12 w-full rounded-full bg-green-night px-6 text-sm font-semibold uppercase tracking-wide text-white shadow-soft hover:bg-green-night/90"
            aria-label={t("searchResults.deliveryBanner.ctaAria")}
          >
            {t("searchResults.deliveryBanner.cta")}
            <ArrowRight className="ml-2 h-4 w-4 shrink-0" aria-hidden />
          </Button>
        </div>

        {/* Desktop */}
        <div className="hidden lg:grid lg:grid-cols-[minmax(0,1.05fr)_auto_minmax(0,0.9fr)_minmax(300px,340px)_minmax(0,1fr)] lg:items-center lg:gap-x-5 xl:gap-x-8">
          <div className="min-w-0">
            {headline}
            <Button
              type="button"
              onClick={handleBookNow}
              className="mt-4 h-11 rounded-md bg-green-night px-5 text-sm font-semibold uppercase tracking-wide text-white shadow-soft hover:bg-green-night/90"
              aria-label={t("searchResults.deliveryBanner.ctaAria")}
            >
              {t("searchResults.deliveryBanner.cta")}
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
            </Button>
          </div>

          <MoroccoDeliveryMap className="h-[172px] w-[210px] xl:h-[188px] xl:w-[230px]" />

          <ul className="flex flex-col gap-3">
            {locations.map(({ icon, titleKey }) => (
              <FeatureItem
                key={titleKey}
                icon={icon}
                title={t(`searchResults.deliveryBanner.locations.${titleKey}.title`)}
                subtitle={t(`searchResults.deliveryBanner.locations.${titleKey}.subtitle`)}
              />
            ))}
          </ul>

          <DeliveryBannerCar alt={carAlt} className="w-[300px] xl:w-[320px]" />

          <ul className="flex flex-col gap-3">
            {benefits.map(({ icon, key }) => (
              <FeatureItem
                key={key}
                icon={icon}
                title={t(`searchResults.deliveryBanner.benefits.${key}.title`)}
                subtitle={t(`searchResults.deliveryBanner.benefits.${key}.subtitle`)}
              />
            ))}
          </ul>
        </div>
    </>
  );

  if (isHomeSection) {
    return (
      <section
        id="delivery-banner"
        className={cn(
          "relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 px-2 py-5 sm:px-3 sm:py-7 lg:px-4",
          className
        )}
        aria-label={t("searchResults.deliveryBanner.ariaLabel")}
      >
        <div className="w-full overflow-hidden rounded-2xl border border-border/70 bg-white shadow-soft sm:rounded-3xl">
          <div className="px-3 py-5 sm:px-6 sm:py-8 lg:px-10 lg:py-12 xl:px-12 xl:py-14">
            {content}
          </div>
        </div>
      </section>
    );
  }

  return (
    <aside
      className={cn(
        "relative w-full rounded-2xl border border-border/70 bg-white shadow-soft sm:rounded-3xl",
        "mb-5 sm:mb-8",
        className
      )}
      aria-label={t("searchResults.deliveryBanner.ariaLabel")}
    >
      <div className="relative p-3 sm:p-5 lg:p-6 xl:p-7">{content}</div>
    </aside>
  );
};

export default SearchResultsDeliveryBanner;
